import { useState } from 'react';
import { GrClose, GrExpand } from 'react-icons/gr';
// (No utils imports needed now — the instructions copy lists the two
// required columns explicitly. If the alias set is widened later, this
// component can be expanded to enumerate them dynamically again.)

interface CollapsibleInstructionsProps {
  className?: string;
  /**
   * ATAD-136: 'zip' shows the ZIP code file requirements and template.
   * ATAD-146: 'model' shows what a CMDA model file holds. There is no
   * template: model files come from Alliant, not from the user.
   * 'id-source' is a First Party or Syndicated Alliant file: IDs only, also
   * from Alliant.
   */
  kind?: 'id' | 'zip' | 'model' | 'id-source';
}

const TEMPLATE_HREFS: Record<string, string | null> = {
  id: '/file_template.csv',
  zip: '/zip_code_template.csv',
  model: null,
  'id-source': null,
};

export function CollapsibleInstructions({
  className = '',
  kind = 'id',
}: CollapsibleInstructionsProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const templateHref = TEMPLATE_HREFS[kind] ?? null;

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className={`bg-green-50 border border-green-200 rounded-md p-4 ${className}`}>
      <div className="flex justify-between items-start">
        <div className="flex items-start">
          <div className="text-green-600 mr-3 flex-shrink-0 mt-0.5">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="text-blue-500"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div>
            <p className="font-semibold text-sm text-blue-700">File must be CSV format</p>
          </div>
        </div>
        <button
          type="button"
          onClick={toggleExpanded}
          className="text-blue-600 hover:text-blue-800 transition-colors p-1 hover:bg-blue-100 rounded"
        >
          {isExpanded ? (
            <GrClose size={16} />
          ) : (
            <GrExpand size={16} />
          )}
        </button>
      </div>

      {isExpanded && (
        <div className="mt-4 space-y-4">
          {kind === 'model' ? (
            <div>
              <p className="text-sm text-gray-700 mb-2">Columns:</p>
              <p className="text-xs text-gray-600 font-mono bg-white p-2 rounded border border-gray-200">
                AIQ_HHID, AIQ_INDID, Model_Score
              </p>
              <ul className="text-xs text-gray-500 mt-2 list-disc list-inside text-left space-y-1">
                <li>
                  The score column can also be named VIGESIMAL. Header names are
                  matched ignoring case, spaces and underscores.
                </li>
                <li>Scores are whole numbers from 1 to 20, where 1 = best.</li>
                <li>Files are .csv or .csv.gz (gzip-compressed CSV).</li>
                <li>
                  Files appear here when Alliant drops them in the shared folder.
                  Importing makes your own copy; the file stays in the folder.
                </li>
                <li>
                  Each import takes a few minutes. The model then shows up in the
                  Audience Builder under Model Files.
                </li>
              </ul>
            </div>
          ) : kind === 'id-source' ? (
            <div>
              <p className="text-sm text-gray-700 mb-2">Columns:</p>
              <p className="text-xs text-gray-600 font-mono bg-white p-2 rounded border border-gray-200">
                AIQ_HHID, AIQ_INDID
              </p>
              <ul className="text-xs text-gray-500 mt-2 list-disc list-inside text-left space-y-1">
                <li>
                  CSV with AIQ_HHID and AIQ_INDID; extra columns are ignored.
                  Header names are matched ignoring case, spaces and underscores.
                </li>
                <li>Files are .csv or .csv.gz (gzip-compressed CSV).</li>
                <li>
                  Files appear here when Alliant drops them in the shared folder.
                  Importing makes your own copy; the file stays in the folder.
                </li>
                <li>
                  Each import takes a few minutes and matches the IDs to people.
                  The file then shows up under Uploaded Files, ready for the
                  Audience Builder, Enhancements and Insights.
                </li>
              </ul>
            </div>
          ) : kind === 'zip' ? (
            <div>
              <p className="text-sm text-gray-700 mb-2">One ZIP code per row:</p>
              <p className="text-xs text-gray-600 font-mono bg-white p-2 rounded border border-blue-100">
                zip
                <br />
                02138
                <br />
                80202
              </p>
              <ul className="text-xs text-gray-500 mt-2 list-disc list-inside text-left space-y-1">
                <li>
                  A header is optional. If your file has several columns, name the ZIP
                  column &quot;zip&quot;, &quot;zip code&quot; or &quot;zip5&quot;; other
                  columns are ignored.
                </li>
                <li>
                  ZIP+4 (80202-1234) is accepted, and leading zeros dropped by Excel
                  (2138) are restored.
                </li>
                <li>
                  Rows that aren&apos;t a valid ZIP code are skipped and reported after
                  upload.
                </li>
                <li>Up to 10,000 unique ZIP codes and 10 MB per file.</li>
              </ul>
            </div>
          ) : (
            <div>
              <p className="text-sm text-gray-700 mb-2">Required columns (exactly these two):</p>
              <p className="text-xs text-gray-600 font-mono bg-white p-2 rounded border border-blue-100">
                aiq_hhid, aiq_indid
              </p>
              <p className="text-xs text-gray-500 mt-2">
                Your CSV must contain only these two columns. Files with extra columns
                or missing either identifier will be rejected.
              </p>
            </div>
          )}

          <div className="flex items-center justify-between pt-2 border-t border-blue-100">
            {templateHref ? (
              <div className="flex items-center text-blue-600 text-sm font-medium hover:text-blue-800 transition-colors cursor-pointer">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="mr-1.5"
                >
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
                </svg>
                <a
                  href={templateHref}
                  target="_blank"
                  className="hover:underline"
                >
                  Download template
                </a>
              </div>
            ) : (
              <span />
            )}
            <button
              type="button"
              onClick={toggleExpanded}
              className="text-blue-600 text-sm font-medium hover:text-blue-800 hover:underline"
            >
              Hide field requirements
            </button>
          </div>
        </div>
      )}

      {!isExpanded && (
        <div className="flex justify-between items-center mt-3">
          {templateHref ? (
            <a
              href={templateHref}
              target="_blank"
              className="flex items-center text-blue-600 text-sm font-medium hover:text-blue-800 hover:underline"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="mr-1.5"
              >
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
              </svg>
              Download template
            </a>
          ) : (
            <span />
          )}
          <button
            type="button"
            onClick={toggleExpanded}
            className="text-blue-600 text-sm font-medium hover:text-blue-800 hover:underline p-2"
          >
            Show field requirements
          </button>
        </div>
      )}
    </div>
  );
}

export default CollapsibleInstructions;